import TextButton from 'LuxKit/Button/TextButton'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import LoyaltyBenefitItemTile from 'loyalty/components/LoyaltyBenefitItemTile'
import LoyaltyBenefitCompactRow from 'loyalty/components/LoyaltyBenefitCompactRow'
import LoyaltyEsimDetailsModal from 'loyalty/components/LoyaltyEsimDetailsModal'
import LoyaltyTierMessageBanner from 'loyalty/components/LoyaltyTierMessageBanner'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import { getEsimGbForTier, isPastEsimTripEnd, isWithinEsimRedemptionWindow } from 'loyalty/lib/loyaltyEsimUtils'
import Group from 'components/utils/Group'
import { useLoyaltyEsimImage } from 'loyalty/constants/loyaltyEsimConstants'
import useEsimBenefitTileMessages from 'loyalty/hooks/useEsimBenefitTileMessages'
import getLoyaltyAccountTier from 'loyalty/selectors/getLoyaltyAccountTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'

interface Props {
  order: App.Order
  layout?: 'tile' | 'row'
}

function LoyaltyOrderViewBenefitsEsimItem({ order, layout = 'tile' }: Props) {
  const intl = useIntl()
  const currentTier = useAppSelector(getLoyaltyAccountTier)
  const loyaltyConfig = useAppSelector(state => state.loyalty.config)
  const orderEsimBenefit = order.loyaltyDetails.esimBenefit
  const esimAllowance = useMemo(() => {
    if (!orderEsimBenefit) return undefined

    const includedGb = getEsimGbForTier(loyaltyConfig, orderEsimBenefit.tier)
    if (includedGb === undefined) return undefined

    return {
      tier: orderEsimBenefit.tier,
      includedGb,
      isHistoricalTier: !!currentTier && orderEsimBenefit.tier !== currentTier,
    }
  }, [currentTier, loyaltyConfig, orderEsimBenefit])
  const gbValue = esimAllowance?.includedGb
  const showModal = useContext(ModalContext)
  const esimImage = useLoyaltyEsimImage()

  const tripEnded = isPastEsimTripEnd(orderEsimBenefit?.endDate)
  const isWithinRedemptionWindow = useMemo(
    () => isWithinEsimRedemptionWindow(
      orderEsimBenefit?.departureDate ?? undefined,
      orderEsimBenefit?.endDate ?? undefined,
    ),
    [orderEsimBenefit?.departureDate, orderEsimBenefit?.endDate],
  )

  const { heading, description, addedLabel, detailsLabel } = useEsimBenefitTileMessages(gbValue ?? 0)

  const onActionClick = useCallback(() => {
    showModal(<LoyaltyEsimDetailsModal orderId={order.id} />)
  }, [showModal, order.id])

  if (!esimAllowance) return null

  const includedGb = esimAllowance.includedGb

  const availabilityNote = intl.formatMessage({
    id: 'bR0Po-PE5ozKbAcLohD7',
    defaultMessage: 'Activates 7 days before departure',
    description: 'eSIM availability note showing activation timeline',
  })

  const redeemLabel = intl.formatMessage({
    id: 'VHz2lQb8PWn8-_kQU5_-',
    defaultMessage: 'Redeem',
    description: 'Button to open eSIM details/redeem modal on order and booking confirmation',
  })
  const complimentaryGbThisTripLabel = intl.formatMessage(
    {
      id: '0E7T9VS_Hx1WOA6xxF-X',
      defaultMessage: '{amount}GB for this trip',
      description: 'Label showing eSIM data allowance for this trip for account benefit tile',
    },
    { amount: includedGb },
  )
  const historicalTierLabel = esimAllowance.isHistoricalTier ? intl.formatMessage(
    {
      id: 'aM4jB2hzoUP2a3KXy14H',
      defaultMessage: 'Added when you were {tierName}',
      description: 'Pill copy on an order eSIM benefit when the included allowance came from the member’s older tier',
    },
    { tierName: getLoyaltyTierDisplayName(esimAllowance.tier) },
  ) : undefined

  if (tripEnded) return null

  const actionButton = (
    <TextButton kind="secondary" onClick={onActionClick}>
      {isWithinRedemptionWindow ? redeemLabel : detailsLabel}
    </TextButton>
  )

  if (layout === 'row') {
    const rowHeading = intl.formatMessage(
      { id: '7mK-OaPGeHWvL6w2E7q-', defaultMessage: '{gb} GB International Data', description: 'eSIM data benefit label on the upcoming escapes card' },
      { gb: includedGb },
    )
    const activationNote = intl.formatMessage({
      id: 'z3xS9H2PqLS5EP2t21gK',
      defaultMessage: 'Activate 7 days before departure',
      description: 'eSIM activation timing note on the upcoming escapes card',
    })
    const viewLabel = intl.formatMessage({
      id: '2PotyrTgljknniT_VfLn',
      defaultMessage: 'View',
      description: 'Button to view eSIM benefit details on the upcoming escapes card',
    })
    return (
      <LoyaltyBenefitCompactRow
        benefitType="esim_package"
        heading={rowHeading}
        status={!isWithinRedemptionWindow && (
          <BodyText variant="medium" colour="highlight-primary-dark" weight="bold">
            {activationNote}
          </BodyText>
        )}
        action={
          <TextButton kind="secondary" variant="default" size="small" shape="round" onClick={onActionClick}>
            {isWithinRedemptionWindow ? redeemLabel : viewLabel}
          </TextButton>
        }
      />
    )
  }

  return (
    <LoyaltyBenefitItemTile
      heading={heading}
      labels={
        <>
          {esimAllowance.isHistoricalTier && (
            <LoyaltyTierLabel kind="primary" tier={esimAllowance.tier}>
              {complimentaryGbThisTripLabel}
            </LoyaltyTierLabel>
          )}
          <StatusLabel variant="confirmation" startIcon={<LineCheckCircleIcon />}>
            {addedLabel}
          </StatusLabel>
        </>
      }
      image={esimImage}
      actions={actionButton}
    >
      <Group direction="vertical" gap={12}>
        <BodyText variant="medium">{description}</BodyText>
        {!isWithinRedemptionWindow && (
          <BodyText variant="medium" colour="highlight-primary-dark" weight="bold">
            {availabilityNote}
          </BodyText>
        )}
        {esimAllowance.isHistoricalTier && historicalTierLabel && (
          <LoyaltyTierMessageBanner tier={esimAllowance.tier} density="compact" icon={<LoyaltyTierIcon tier={esimAllowance.tier} size="M" />}>
            {historicalTierLabel}
          </LoyaltyTierMessageBanner>
        )}
      </Group>
    </LoyaltyBenefitItemTile>
  )
}

export default LoyaltyOrderViewBenefitsEsimItem
